import React from 'react';
import moment from 'moment';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Axios from 'axios';
import { CloseIcon, EditIcon, UserIcon } from 'assets/icons';
import { Constants, Globals } from 'utils';
import { DocBox } from './components';

interface ModalViewDoctorProps {
  user?: any
  onClose: () => void
  onEdit?: () => void
  onApprove?: () => void
  onReject?: () => void
  onDisable?: () => void
}

const ModalViewDoctor = ({ user, onClose, onEdit, onApprove, onReject, onDisable }: ModalViewDoctorProps) => {
  // const canEnable = user?.status === Constants.USER.STATUS.INACTIVE && user?.verified === Constants.USER.VERIFIED.VERIFIED;
  const canDisable = user?.status === Constants.USER.STATUS.ACTIVE && user?.verified === Constants.USER.VERIFIED.VERIFIED;
  const photo = user?.photo ? Globals.fromStorage(user.photo) : UserIcon;

  const onDownload = async () => {
    const files: { name: string, ext: string, url: string }[] = [];

    if (user?.document_photo) {
      files.push({
        name: 'Cédula de identidad',
        ext: Globals.getFileExtension(user.document_photo),
        url: Globals.fromStorage(user.document_photo),
      });
    }

    if (user?.medic_document_photo) {
      files.push({
        name: 'Carnet Médico',
        ext: Globals.getFileExtension(user.medic_document_photo),
        url: Globals.fromStorage(user.medic_document_photo),
      });
    }

    if (user?.university_degree_photo) {
      files.push({
        name: 'Título Universitario',
        ext: Globals.getFileExtension(user.university_degree_photo),
        url: Globals.fromStorage(user.university_degree_photo),
      });
    }

    if (user?.article_8_photo) {
      files.push({
        name: 'Artículo N°8',
        ext: Globals.getFileExtension(user.article_8_photo),
        url: Globals.fromStorage(user.article_8_photo),
      });
    }

    if (user?.culmination_photo) {
      files.push({
        name: 'Carta de Culminación',
        ext: Globals.getFileExtension(user.culmination_photo),
        url: Globals.fromStorage(user.culmination_photo),
      });
    }

    if (user?.contract) {
      files.push({
        name: 'Contrato',
        ext: Globals.getFileExtension(user.contract),
        url: Globals.fromStorage(user.contract),
      });
    }

    if (!files.length)
      return Globals.showError('No tiene archivos adjuntos');

    const zip = new JSZip();

    const arrOfFiles = files.map(async item => {
      const resp = await Axios.get(item.url, { responseType: 'blob' });
      zip.file(item.name + '.' + item.ext, resp.data);
    });

    Promise.all(arrOfFiles)
      .then(() => {
        zip.generateAsync({ type: "blob" })
          .then(blob => saveAs(blob, `${user?.full_name}.zip`));
      })
      .catch(console.log);
  }

  return (
    <div>
      <button className="close-btn" onClick={onClose}>
        <img src={CloseIcon} alt="Cerrar" />
      </button>

      <section className="head">
        <div className="user-photo">
          <img src={photo} alt="Usuario" />
        </div>
        <div style={{ gridColumnStart: 2, gridColumnEnd: 4 }}>
          <p className="user-name">{ user?.full_name }</p>
          <p className="user-document">{ user?.full_document }</p>
        </div>
        <div className="actions">
          {onEdit && (
            <button onClick={onEdit}>
              <img src={EditIcon} alt="Editar" />
              <span>Editar</span>
            </button>
          )}
        </div>

        <div />

        <div>
          <p>Especialidad:</p>
          <p>{ user?.specialty?.name }</p>
        </div>
        <div>
          <p>Experiencia (años):</p>
          <p>{ user?.experience }</p>
        </div>
      </section>

      <section className="body">
        <div className="details">
          <div>
            <label>Fecha de registro:</label>
            <p>{ moment(user?.created_at).format('DD-MM-YYYY') }</p>
          </div>
          <div>
            <label>Teléfono:</label>
            <p>{ user?.full_phone }</p>
          </div>
          <div>
            <label>Correo:</label>
            <p>{ user?.email }</p>
          </div>
          <div className="user-address">
            <label>Dirección:</label>
            <p>{ user?.address }</p>
          </div>
          <DocBox
            label="Cédula de identidad"
            file={user?.document_photo ? Globals.fromStorage(user.document_photo) : ''}
            fileName="Documento"
            isPDF={user?.document_photo?.includes('.pdf')}
            canDownload={!!user?.document_photo}
          />
          <DocBox
            label="Carnet Médico"
            file={user?.medic_document_photo ? Globals.fromStorage(user.medic_document_photo) : ''}
            fileName="Documento"
            isPDF={user?.medic_document_photo?.includes('.pdf')}
            canDownload={!!user?.medic_document_photo}
          />
          <DocBox
            label="Título Universitario"
            file={user?.university_degree_photo ? Globals.fromStorage(user.university_degree_photo) : ''}
            fileName="Documento"
            isPDF={user?.university_degree_photo?.includes('.pdf')}
            canDownload={!!user?.university_degree_photo}
          />
          <DocBox
            label="Artículo N°8"
            file={user?.article_8_photo ? Globals.fromStorage(user.article_8_photo) : ''}
            fileName="Documento"
            isPDF={user?.article_8_photo?.includes('.pdf')}
            canDownload={!!user?.article_8_photo}
          />
          <DocBox
            label="Carta de Culminación"
            file={user?.culmination_photo ? Globals.fromStorage(user.culmination_photo) : ''}
            fileName="Documento"
            isPDF={user?.culmination_photo?.includes('.pdf')}
            canDownload={!!user?.culmination_photo}
          />
          <DocBox
            label="Contrato"
            file={user?.contract ? Globals.fromStorage(user.contract) : ''}
            fileName="Contrato"
            isPDF={user?.contract?.includes('.pdf')}
            canDownload={!!user?.contract}
          />
        </div>

        <div className="actions">
          <button className="blue" onClick={onDownload}>Descargar</button>
          <div>
            {(canDisable && onDisable) && (
              <button className="red" onClick={onDisable}>Bloquear</button>
            )}
            {user?.verified === Constants.USER.VERIFIED.IN_PROCESS && (
              <>
                {onApprove && (
                  <button className="green" onClick={onApprove}>Aprobar</button>
                )}
                {onReject && (
                  <button className="red" onClick={onReject}>Rechazar</button>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default ModalViewDoctor;